import { useEffect, useRef, useState } from 'react';
import kit from './kit.module.css';
import styles from './ConfidenceDisclosureDemoAgentic.module.css';

type Stage = 'searching' | 'pick' | 'who' | 'booking' | 'done';
type Flight = 'lisbon' | 'direct';
type Who = 'me' | 'both';

interface LogEntry {
  id: number;
  time: string;
  text: string;
  state: 'done' | 'active' | 'error';
  tag?: string;
}

const FLIGHT_LABEL: Record<Flight, string> = {
  lisbon: '14:05 via Lisbon',
  direct: '16:40 direct',
};

const DOT: Record<LogEntry['state'], string> = {
  done: kit.logDotDone,
  active: kit.logDotActive,
  error: kit.logDotError,
};

export function ConfidenceDisclosureDemoAgentic() {
  const [stage, setStage] = useState<Stage>('searching');
  const [flight, setFlight] = useState<Flight | null>(null);
  const [showVerify, setShowVerify] = useState(false);
  const [log, setLog] = useState<LogEntry[]>([
    { id: 1, time: '18:02', text: 'Flight to Porto canceled by the airline', state: 'error' },
    { id: 2, time: '18:02', text: 'Searching replacement flights', state: 'active' },
  ]);
  const [feedback, setFeedback] = useState('The agent is looking for a replacement. Watch how it reports doubt.');
  const timers = useRef<ReturnType<typeof setTimeout>[]>([]);

  useEffect(() => {
    const list = timers.current;
    list.push(
      setTimeout(() => {
        setLog((l) =>
          l.map((e) => (e.id === 2 ? { ...e, text: 'Found 2 options that arrive today', state: 'done' } : e))
        );
        setStage('pick');
        setFeedback('It names the best match, says why it is unsure, and offers a safer option. Pick one.');
      }, 1300)
    );
    return () => list.forEach(clearTimeout);
  }, []);

  const pickFlight = (f: Flight) => {
    setFlight(f);
    setStage('who');
    setFeedback(
      f === 'lisbon'
        ? 'You took the riskier pick knowingly. A shared itinerary is involved, so it asks before rebooking.'
        : 'Safer option chosen. A colleague shares this trip, so the agent asks instead of assuming.'
    );
  };

  const book = (who: Who) => {
    if (!flight) return;
    setStage('booking');
    setFeedback('Rebooking only what you chose...');
    setLog((l) => [...l, { id: 3, time: '18:05', text: `Rebooking ${FLIGHT_LABEL[flight]}`, state: 'active' }]);
    timers.current.push(
      setTimeout(() => {
        const notChanged =
          who === 'me' ? 'hotel, return flight, Mara’s booking' : 'hotel, both return flights';
        const whoText = who === 'me' ? 'your outbound' : 'your and Mara’s outbounds';
        setLog((l) =>
          l.map((e) =>
            e.id === 3
              ? {
                  ...e,
                  time: '18:06',
                  text: `Rebooked ${whoText} to ${FLIGHT_LABEL[flight]}. Not changed: ${notChanged}`,
                  state: 'done',
                  tag: who === 'both' ? 'Mara notified' : undefined,
                }
              : e
          )
        );
        setStage('done');
        setFeedback(
          who === 'me'
            ? 'Done. The log line states the scope, so you know Mara’s trip was left alone.'
            : 'Done. Mara was included only because you said so, and she gets a note explaining why.'
        );
      }, 1100)
    );
  };

  return (
    <div className={kit.root}>
      <div className={kit.log} aria-label="Agent activity">
        {log.map((e) => (
          <div key={e.id} className={kit.logRow}>
            <span className={kit.logTime}>{e.time}</span>
            <span className={`${kit.logDot} ${DOT[e.state]}`} aria-hidden="true" />
            <span>{e.text}</span>
            {e.tag && <span className={kit.chip}>{e.tag}</span>}
          </div>
        ))}
      </div>

      {stage === 'pick' && (
        <div className={kit.proposal}>
          <div className={kit.proposalTop}>
            <span>Replacement flight</span>
            <span>Needs your pick</span>
          </div>
          <div className={styles.options}>
            <div className={styles.option}>
              <div className={kit.row}>
                <strong>14:05 via Lisbon</strong>
                <span className={`${kit.chip} ${kit.chipWarn}`}>Unsure</span>
              </div>
              <p className={styles.note}>
                Best match, but the connection is 50 minutes and I couldn't confirm the arrival terminal.
              </p>
            </div>
            <div className={styles.option}>
              <div className={kit.row}>
                <strong>16:40 direct</strong>
                <span className={kit.chip}>Safer</span>
              </div>
              <p className={styles.note}>No connection. Arrives 2 hours later, at 19:15.</p>
            </div>
          </div>

          {showVerify && (
            <p className={`${styles.note} ${styles.verify}`}>
              To check it yourself: Lisbon gate info posts about 3 hours before departure. If the Porto leg
              leaves from Terminal 2, you would need to change terminals by shuttle.
            </p>
          )}

          <div className={`${kit.row} ${styles.actions}`}>
            <button type="button" className={`${kit.btn} ${kit.btnEdit}`} onClick={() => pickFlight('lisbon')}>
              Book 14:05
            </button>
            <button type="button" className={`${kit.btn} ${kit.btnApprove}`} onClick={() => pickFlight('direct')}>
              Book 16:40 direct
            </button>
            {!showVerify && (
              <button
                type="button"
                className={`${kit.btn} ${kit.btnGhost}`}
                onClick={() => {
                  setShowVerify(true);
                  setFeedback('Instead of a score, the agent gives you a concrete way to verify the doubt.');
                }}
              >
                How can I check?
              </button>
            )}
          </div>
        </div>
      )}

      {stage === 'who' && flight && (
        <div className={kit.proposal}>
          <div className={kit.proposalTop}>
            <span>Shared itinerary</span>
            <span>{FLIGHT_LABEL[flight]}</span>
          </div>
          <h3 className={kit.proposalTitle}>Rebook only you, or both of you?</h3>
          <dl className={kit.fields}>
            <dt>Also on it</dt>
            <dd>Mara Ives - currently on the canceled flight</dd>
            <dt>Untouched</dt>
            <dd>Hotel and return flights, either way</dd>
          </dl>
          <div className={`${kit.row} ${styles.actions}`}>
            <button type="button" className={`${kit.btn} ${kit.btnApprove}`} onClick={() => book('me')}>
              Only me
            </button>
            <button type="button" className={`${kit.btn} ${kit.btnEdit}`} onClick={() => book('both')}>
              Both of us
            </button>
            <button
              type="button"
              className={`${kit.btn} ${kit.btnGhost}`}
              onClick={() => {
                setStage('pick');
                setFeedback('Back to the options. Nothing has been booked.');
              }}
            >
              Back
            </button>
          </div>
        </div>
      )}

      <p className={kit.feedback} aria-live="polite">
        {feedback}
      </p>
    </div>
  );
}
